import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { ReviewApi } from 'api';
import type { Review } from 'api/methods/ReviewApi';

/**
 * [Review] Query key 생성
 * --
 */
export const ReviewQueryKeys = createQueryKeys('review', {
  getReview: (idx: number, params?: { [key: string]: string | number }) => [
    idx,
    params,
  ],
  getReviews: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [Review] 후기 전체 조회
 * --
 */
export const useGetReviews = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: ReviewQueryKeys.getReviews(params).queryKey,
    queryFn: () => ReviewApi.getReviews(params),
    ...options,
  });
  return data;
};

/**
 * [Review] 후기 상세 조회
 * --
 */
export const useGetReview = (
  idx: number,
  params?: { [key: string]: string | number }
) => {
  const data = useQuery({
    queryKey: ReviewQueryKeys.getReview(idx, params).queryKey,
    queryFn: () => ReviewApi.getReview(idx, params),
  });

  return data;
};

/**
 * [Review] 후기 생성
 * --
 */
export const useCreateReview = (body: Review) => {
  const { mutateAsync: createReview, ...result } = useMutation({
    mutationFn: (variables: Review) => ReviewApi.createReview(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { createReview, ...result };
};

/**
 * [Review] 후기 수정
 * --
 */
export const useUpdateReview = (body: Review) => {
  const { mutateAsync: updateReview, ...result } = useMutation({
    mutationFn: (variables: Review) => ReviewApi.updateReview(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { updateReview, ...result };
};

/**
 * [Review] 후기 삭제
 * --
 */
export const useDeleteReview = (idx: number) => {
  const { mutateAsync: deleteReview, ...result } = useMutation({
    mutationFn: (variables: number) => ReviewApi.deleteReview(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { deleteReview, ...result };
};

/**
 * [Review] 후기 수정
 * --
 */
export const useUpsertReview = () => {
  const { mutateAsync: upsertReview, ...result } = useMutation({
    mutationFn: (variables: Review[]) => ReviewApi.upsertReview(variables),
  });

  return { upsertReview, ...result };
};

/**
 * [Review] 후기 삭제
 * --
 */
export const useBulkDeleteReview = () => {
  const { mutateAsync: bulkDeleteReview, ...result } = useMutation({
    mutationFn: (variables: Review[]) => ReviewApi.bulkDeleteReview(variables),
  });

  return { bulkDeleteReview, ...result };
};
